import React, { useCallback, useEffect, useState } from "react";

import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";

import { activateOnEnterOrSpace } from "../utils/keyboardActivation";

const textValue = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    return String(value.value || value.text || value.name || "");
  }

  return String(value);
};

const heroMetaLine = (item) => {
  const genre = textValue(item.genre).split(",")[0].trim();
  const mediaType = item.mediaType || item.type;
  const typeLabel =
    mediaType === "movie"
      ? "Movie"
      : mediaType === "tv" || mediaType === "series"
        ? "Tv"
        : textValue(mediaType);

  return [genre, textValue(item.year), typeLabel].filter(Boolean).join(" • ");
};

const EmblaCarousel = ({ items = [] }) => {
  const navigate = useNavigate();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
    dragFree: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Auto-play
  useEffect(() => {
    if (!emblaApi) return;

    const autoplay = setInterval(() => {
      emblaApi.scrollNext();
    }, 7000);

    return () => clearInterval(autoplay);
  }, [emblaApi]);

  if (!items || items.length === 0) return null;

  return (
    <div className="relative">
      <style>
        {`
          /* Edge-to-edge — extends behind the fixed left nav rail */
          .hero-carousel-container {
            margin-left: 0;
            margin-right: 0;
            width: 100vw;
          }
          /* Hero scrims: 25% top tint, 55% left vignette, and the bottom half
             on desktop (phones blend the bottom 36% into the page, same as iOS). */
          .hero-left-vignette,
          .hero-top-fade,
          .hero-scrim-bottom,
          .hero-bottom-fade {
            position: absolute;
            pointer-events: none;
            z-index: 2;
          }
          .hero-left-vignette {
            top: 0;
            bottom: 0;
            left: 0;
            width: 55%;
            background: linear-gradient(
              to right,
              #000 0%,
              rgba(0, 0, 0, 0.5) 50%,
              transparent 100%
            );
          }
          .hero-top-fade {
            top: 0;
            left: 0;
            right: 0;
            height: 25%;
            background: linear-gradient(to bottom, rgba(0, 0, 0, 0.4), transparent);
          }
          .hero-scrim-bottom {
            left: 0;
            right: 0;
            bottom: 0;
            height: 50%;
            background: linear-gradient(
              to top,
              rgba(0, 0, 0, 0.9) 0%,
              rgba(0, 0, 0, 0.5) 50%,
              transparent 100%
            );
          }
          .hero-bottom-fade {
            left: 0;
            right: 0;
            bottom: 0;
            height: 36%;
            background: linear-gradient(
              to bottom,
              transparent 0%,
              rgba(10, 14, 26, 0.42) 38%,
              rgba(10, 14, 26, 0.76) 76%,
              rgb(10, 14, 26) 100%
            );
          }
          /* Slide-in from left — matches Android slideInFromLeft:
             translationX -40dp -> 0, alpha 0 -> 1, 700ms, DecelerateInterpolator. */
          @keyframes hero-slide-in-left {
            from { transform: translate3d(-40px, 0, 0); opacity: 0; }
            to { transform: translate3d(0, 0, 0); opacity: 1; }
          }
          .hero-slide-element {
            opacity: 0;
            transform: translate3d(-40px, 0, 0);
          }
          .hero-active .hero-slide-element {
            animation: hero-slide-in-left 700ms cubic-bezier(0, 0, 0.2, 1) forwards;
          }
          .hero-active .hero-slide-element[data-stagger="0"] { animation-delay: 0ms; }
          .hero-active .hero-slide-element[data-stagger="1"] { animation-delay: 100ms; }
          .hero-active .hero-slide-element[data-stagger="2"] { animation-delay: 150ms; }
          .hero-active .hero-slide-element[data-stagger="3"] { animation-delay: 200ms; }
          /* On a phone the 255px bottom anchor pushes the logo off the top.
             Pin the block below the weather row instead. */
          .hero-info-block {
            top: calc(28px + 10%);
            bottom: auto;
          }
          .hero-logo-slot {
            display: flex;
            align-items: flex-end;
            height: 96px;
            margin-bottom: 12px;
          }
          .hero-logo {
            max-width: min(280px, 70vw);
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
            object-position: left bottom;
          }
          .hero-logo-slot h1 {
            margin: 0;
            max-height: 100%;
            overflow: hidden;
            font-size: clamp(32px, 4vw, 56px);
            font-weight: 700;
            line-height: 1.05;
          }
          @media (max-width: 1023px) {
            .hero-carousel-container .h-screen {
              height: 100dvh;
            }
            .hero-info-block {
              top: auto;
              bottom: max(64px, 19dvh);
            }
            .hero-logo-slot {
              height: 60px;
              margin-bottom: 8px;
            }
            .hero-description {
              width: auto !important;
              max-width: 300px !important;
              font-size: 12px !important;
              line-height: 1.35 !important;
              max-height: 34px !important;
            }
          }
          @media (min-width: 1024px) and (min-height: 900px) {
            .hero-info-block {
              top: auto;
              bottom: 255px;
            }
            .hero-logo-slot {
              height: 169px;
              margin-bottom: 16px;
            }
            .hero-logo {
              max-width: 541px;
            }
          }
        `}
      </style>
      <div className="hero-carousel-container">
        <div className="overflow-hidden select-none" ref={emblaRef}>
          <div className="flex">
            {items.map((item, index) => {
              const handleClick = () => {
                const mediaId = item.mediaId || item.id || item.stream_id;
                const mediaType =
                  item.mediaType ||
                  item.type ||
                  (item.series_id ? "series" : "movie");

                if (mediaType === "sports") {
                  // Navigate to sports event detail page
                  const eventId = item.idEvent || item.id || mediaId;

                  navigate(`/web-player/sports/event/${eventId}`, {
                    state: { event: item },
                  });
                } else if (mediaId) {
                  if (mediaType === "movie") {
                    navigate(`/web-player/movie/${mediaId}`);
                  } else if (mediaType === "series" || mediaType === "tv") {
                    navigate(`/web-player/series/${mediaId}`);
                  }
                }
              };

              return (
                <div className="flex-[0_0_100%] min-w-0" key={item.id || index}>
                  <div
                    className={`relative h-screen ${index === selectedIndex ? "hero-active" : ""}`}
                    key={`slide-${index}-${selectedIndex === index ? "a" : "i"}`}
                  >
                    <div
                      className="relative h-full overflow-hidden cursor-pointer group"
                      role="button"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)",
                      }}
                      tabIndex={0}
                      onClick={handleClick}
                      onKeyDown={activateOnEnterOrSpace}
                    >
                      {/* Background Image - Prefer backdrop over poster */}
                      {item.backdrop || item.poster || item.thumbnail ? (
                        <img
                          alt={item.title}
                          className="w-full h-full object-cover"
                          src={item.backdrop || item.poster || item.thumbnail}
                          onError={(e) => {
                            e.target.src = "/background.png";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-600 to-purple-600" />
                      )}

                      <div className="hero-top-fade" />
                      <div className="hero-left-vignette" />
                      <div className="hero-scrim-bottom hidden lg:block" />
                      <div className="hero-bottom-fade lg:hidden" />

                      {/* Sports Event - Special Layout */}
                      {item.type === "sports" ? (
                        <>
                          {/* Sport Category Logo - Top Left Corner */}
                          <div className="absolute top-4 sm:top-6 md:top-8 left-3 sm:left-4 md:left-6 flex items-center gap-1 sm:gap-2 z-20">
                            {item.leagueLogo && (
                              <img
                                alt={item.league}
                                className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 object-contain"
                                src={item.leagueLogo}
                              />
                            )}
                            <span className="text-white text-sm sm:text-base md:text-lg lg:text-xl font-semibold uppercase tracking-wide">
                              {item.genre || "SPORTS"}
                            </span>
                          </div>

                          {/* Center Content - Logo for Fighting/Motorsport, Team Logos for other sports */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            {/* Check if this is Fighting or Motorsport category */}
                            {item.genre?.toLowerCase().includes("fighting") ||
                            item.genre?.toLowerCase().includes("motorsport") ? (
                              /* Centered League Logo with Event Name for Fighting/Motorsport */
                              <div className="flex flex-col items-center justify-center gap-4 sm:gap-6 md:gap-8">
                                {item.leagueLogo && (
                                  <img
                                    alt={item.league}
                                    className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 object-contain drop-shadow-2xl"
                                    src={item.leagueLogo}
                                  />
                                )}
                                <div className="flex flex-col items-center gap-2 sm:gap-3">
                                  {/* Show name or title */}
                                  {(item.name || item.title) && (
                                    <h2 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center uppercase tracking-wide drop-shadow-2xl px-4">
                                      {item.name || item.title}
                                    </h2>
                                  )}
                                  {item.metaName && (
                                    <div className="text-white/90 text-base sm:text-lg md:text-xl lg:text-2xl text-center drop-shadow-lg px-4">
                                      {item.metaName}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              /* Team vs Team Layout for traditional sports */
                              <div className="flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8">
                                {/* Home Team Logo */}
                                {item.homeTeamLogo && (
                                  <img
                                    alt={item.homeTeam}
                                    className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 object-contain team-logo"
                                    src={item.homeTeamLogo}
                                  />
                                )}

                                {/* VS or Score - Only show if teams exist */}
                                {item.homeTeam && item.awayTeam && (
                                  <div className="text-center">
                                    <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
                                      <span className="text-white font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[72px]">
                                        {item.homeTeamScore || 0}
                                      </span>
                                      <span className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light mx-1 sm:mx-2">
                                        -
                                      </span>
                                      <span className="text-white font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[72px]">
                                        {item.awayTeamScore || 0}
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {/* Away Team Logo */}
                                {item.awayTeamLogo && (
                                  <img
                                    alt={item.awayTeam}
                                    className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 object-contain team-logo"
                                    src={item.awayTeamLogo}
                                  />
                                )}
                              </div>
                            )}
                          </div>

                          {/* Team Names/Event Name and Meta Name - Bottom Left (Hide for Fighting/Motorsport) */}
                          {!(
                            item.genre?.toLowerCase().includes("fighting") ||
                            item.genre?.toLowerCase().includes("motorsport")
                          ) && (
                            <div
                              className="absolute left-3 sm:left-4 md:left-6"
                              style={{ bottom: "calc(1rem + 20px)" }}
                            >
                              {item.homeTeam && item.awayTeam ? (
                                <>
                                  <h2 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-wide">
                                    {item.homeTeam}
                                  </h2>
                                  <h2 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold uppercase tracking-wide mb-1 sm:mb-2">
                                    {item.awayTeam}
                                  </h2>
                                </>
                              ) : item.name ? (
                                <h2 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide mb-1 sm:mb-2">
                                  {item.name}
                                </h2>
                              ) : null}
                              {item.metaName && (
                                <div className="text-white/80 text-sm sm:text-base md:text-lg lg:text-xl">
                                  {item.metaName}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Status Badge - LIVE */}
                          {item.rating === "LIVE" && (
                            <div className="absolute top-16 sm:top-20 md:top-24 right-4 sm:right-6 md:right-10 bg-red-600 text-white text-sm sm:text-base md:text-lg px-3 sm:px-4 md:px-5 py-1 sm:py-1.5 md:py-2 rounded-lg font-bold animate-pulse">
                              LIVE
                            </div>
                          )}
                        </>
                      ) : (
                        /* Regular Content — sits 255px above carousel bottom
                           (Samsung: bottom: calc(54px + 201px); z-index 10
                           so it overlays the bottom-fade gradient and floats
                           above the cards peeking from below). Left padding
                           clears the 80px fixed nav rail (Samsung uses 132px). */
                        <div
                          className="hero-info-block absolute right-[45%] select-none p-4 pl-20 sm:p-6 sm:pl-20 md:p-8 md:pl-24 lg:pl-[132px] xl:pl-[132px]"
                          style={{ zIndex: 10, left: 0 }}
                        >
                          <div className="flex justify-between items-end">
                            <div className="max-w-3xl">
                              {/* Fixed-height slot so a short logo cannot pull the
                                  rating, watch time, and details button upward. */}
                              <div
                                className="hero-logo-slot hero-slide-element"
                                data-stagger="0"
                              >
                                {item.logo ? (
                                  <img
                                    alt={item.title}
                                    className="hero-logo object-contain object-left"
                                    src={item.logo}
                                  />
                                ) : (
                                  <h1 className="text-white drop-shadow-2xl">
                                    {item.title || item.name}
                                  </h1>
                                )}
                              </div>

                              {/* Rating on the left. Genre, year, and type on the
                                  right, with watch time underneath. */}
                              <div
                                className="hero-slide-element mb-3 flex items-center gap-3"
                                data-stagger="1"
                              >
                                <span className="inline-flex shrink-0 items-center justify-center rounded-md border border-white/80 bg-white/10 px-2.5 py-1 text-xs font-bold text-white">
                                  {textValue(item.rating) || "NR"}
                                </span>
                                <div className="min-w-0 text-sm text-white/90">
                                  <p className="truncate">
                                    {heroMetaLine(item)}
                                  </p>
                                  {textValue(item.duration).trim() &&
                                  textValue(item.duration).trim() !==
                                    "00:00:00" ? (
                                    <p className="mt-1 truncate">
                                      Watch Time: {item.duration}
                                    </p>
                                  ) : null}
                                </div>
                              </div>

                              {/* Description — 2-line clamp + ellipsis, 60% width.
                                  Matches Samsung .hero-carousel__desc:
                                  font-size 18px, line-height 1.4, max-height 54px,
                                  -webkit-line-clamp 2, -webkit-box-orient vertical. */}
                              {item.description && (
                                <p
                                  className="hero-description hero-slide-element drop-shadow-lg"
                                  data-stagger="2"
                                  style={{
                                    maxWidth: "60vw",
                                    width: "60vw",
                                    fontSize: "18px",
                                    color: "rgba(255,255,255,0.7)",
                                    lineHeight: 1.4,
                                    maxHeight: "54px",
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    marginTop: "12px",
                                  }}
                                >
                                  {item.description}
                                </p>
                              )}

                              <button
                                className="hero-slide-element mt-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-2.5 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] backdrop-blur-md"
                                data-stagger="3"
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleClick();
                                }}
                              >
                                <i className="fas fa-circle-info text-sm" />
                                More details
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {scrollSnaps.map((_, index) => (
            <button
              aria-label={`Go to slide ${index + 1}`}
              className={`transition-all duration-300 ${
                index === selectedIndex
                  ? "w-8 h-2 bg-blue-500 rounded-full"
                  : "w-2 h-2 bg-white/40 rounded-full hover:bg-white/60"
              }`}
              key={index}
              onClick={() => scrollTo(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmblaCarousel;
